import { useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useLocaleNavigate from 'utils/customHooks/useLocaleNavigate';
import { useTranslation } from 'react-i18next';
import Cookie from 'js-cookie';
// icons
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
//zustand stores
import { useUserStore, useCartStore, useCompanyStore } from 'stores';
// api
import { loginApi } from 'api/login';
import { getUserPermissions } from 'api/users';
import { getCart, addItemCart } from 'api/ecommerce';
import { buildAddItemCartPayload, getBookConditionFromCartApiLine } from 'utils/bookCondition';
import { getMetadataCompany, getCategoryMenuNotPaginated } from 'api/company';
//components
import Button from 'components/userzone/buttons/ButtonCustom';
import { InputCustom } from 'components/input';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';

const LoginComp = ({ fromCarro = false }) => {
  const { t } = useTranslation(['login', 'backoffice']);
  const navigate = useLocaleNavigate();
  const { addItem, setGiftCard, clear } = useCartStore();
  const {
    loginSuccess,
    fetchUserInfo,
    setIsWorker
  } = useUserStore();
  const { loadMetaCompany, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, email: correo } = useCompanyStore();

  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [passwordType, setPasswordType] = useState('password');
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const { assignedLibrary, libraryPath } = useAssignedLibrary();

  const onLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      setValidated(true);
      event.stopPropagation();
      return;
    }

    setInvalidCredentials(false);
    setEmailError(false);
    setPasswordError(false);
    setLoginError(false);
    if (!emailInput) {
      setEmailError(true);
    }
    if (!password) {
      setPasswordError(true);
    }
    if (!emailInput || !password) {
      return;
    }
    const payload = { username: emailInput, password };
    try {
      const { status, data } = await loginApi(payload);
      if (status === 200) {
        localStorage.setItem('access', data.access);
        localStorage.setItem('refresh', data.refresh);
        localStorage.setItem('type_user', data.type_user);
        localStorage.setItem('permissions', data.permissions);
        Cookie.set('access', data.access, { sameSite: 'strict' });
        Cookie.set('refresh', data.refresh, { sameSite: 'strict' });
        Cookie.set('type_user', data.type_user, { sameSite: 'strict' });
        Cookie.set('permissions', data.permissions, { sameSite: 'strict' });

        await fetchUserInfo();
        // toast.info(t('recuerdaElegirLibreria', { ns: 'login' }) || 'Recuerda ir a "Mi cuenta" para elegir tu librería beneficiada', {
        //   className: 'toast-info-white bg-white',
        //   autoClose: 5000,
        // });
        await loadUserPermissions();
        const metadataCompany = await getMetadataCompany();
        if (metadataCompany.status === 200) {
          const matters = await getCategoryMenuNotPaginated({
            fields: 'id,name,description,genre_ibic,padre_id',
          });
          if (matters.status === 200) {
            const results = {
              ...metadataCompany.data,
              matters: matters.data,
            };
            loadMetaCompany(results);
          } else {
            loadMetaCompany(metadataCompany.data);
          }
        }
        loginSuccess();
        if (data.type_user === 'trabajador') {
          setIsWorker(true);
        } else {
          setIsWorker(false);
        }
        // Obtener isConcourseUser e info_concourse del store después de fetchUserInfo
        const { isConcourseUser, info_concourse } = useUserStore.getState();
        const concourseDiscount = isConcourseUser ? (Number(info_concourse?.discount) || 15) : null;
        const localSnapshot = useCartStore.getState().items.slice();
        const cart = await getCart();
        if (cart.status === 200) {
          clear();
          if (cart.data.items) {
            for (const item of cart.data.items) {
              const fromApi = getBookConditionFromCartApiLine(item);
              const tentativeLocal = localSnapshot.find((li) => li.id === item.producto?.id);
              const localCond =
                tentativeLocal?.book_condition !== undefined &&
                tentativeLocal?.book_condition !== null &&
                tentativeLocal?.book_condition !== ''
                  ? tentativeLocal.book_condition
                  : undefined;
              const cond = localCond ?? fromApi ?? item.producto?.book_condition;
              const localMatch =
                localSnapshot.find(
                  (li) =>
                    li.id === item.producto?.id &&
                    (cond == null || cond === '' || (li.book_condition ?? null) === (cond ?? null))
                ) ?? tentativeLocal;
              let product =
                item.producto && typeof item.producto === 'object'
                  ? { ...item.producto, ...(cond != null && cond !== '' ? { book_condition: cond } : {}) }
                  : item.producto;
              if (product && typeof product === 'object' && localMatch) {
                product = {
                  ...product,
                  type: product.type || localMatch.type,
                  ...(product.has_used == null && localMatch.has_used != null
                    ? { has_used: localMatch.has_used }
                    : {}),
                  ...(product.has_newbalance == null && localMatch.has_newbalance != null
                    ? { has_newbalance: localMatch.has_newbalance }
                    : {}),
                };
              }
              if (product && typeof product === 'object' && item.unit_price != null) {
                product = { ...product, unit_price: item.unit_price, price: item.unit_price };
              }
              addItem({
                product,
                province: cart?.data?.shipping_address?.province?.label || cart?.data?.shipping_address?.province || '',
                shippingCost: shipping_cost,
                shippingCostIslands: shipping_cost_islands,
                minPurchaseForFreeShipping: min_purchase_for_free_shipping,
                quantity: item.quantity || 1,
                setQuantity: true,
                isConcourseUser,
                concourseDiscount,
                bookCondition: cond,
              });
            }
          }
          // Procesar gift card si existe y tiene un monto válido
          if (cart.data.giftcard_purchased && Object.keys(cart.data.giftcard_purchased).length > 0 && cart.data.giftcard_purchased.amount > 0) {
            setGiftCard({
              id: cart.data.giftcard_purchased.id,
              code: cart.data.giftcard_purchased.code,
              amount: cart.data.giftcard_purchased.amount,
              discount_amount: cart.data.giftcard_purchased.amount
            });
          }

          for (const line of localSnapshot) {
            await addItemCart(buildAddItemCartPayload(line.id, line.quantity, line, line.book_condition));
          }
        } else {
          console.log('error');
        }

        if (fromCarro) {
          navigate(libraryPath('envio'), { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else if (status === 401) {
        setInvalidCredentials(true);
      } else {
        setLoginError(true);
        console.log('Error: ' + data.detail);
      }
    } catch (error) {
      setLoginError(true);
      console.log('Error de conexión:', error);
    }
    setLoading(false);
  };

  async function loadUserPermissions() {
    try {
      const permissions = await getUserPermissions();
      localStorage.setItem('user_permissions', JSON.stringify(permissions));
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
  }

  const handleIconType =
    passwordType === 'password' ? (
      <IoEyeOutline
        className={`position-absolute end-0 translate-middle-y mx-3 cursor-pointer ${password !== '' && 'top-50'}`}
        onClick={() => setPasswordType('text')}
      />
    ) : (
      <IoEyeOffOutline
        className={`position-absolute end-0 translate-middle-y mx-3 cursor-pointer ${password !== '' && 'top-50'}`}
        onClick={() => setPasswordType('password')}
      />
    );

  return (
    <Form
      noValidate
      validated={validated}
      onSubmit={onLogin}
      className="login-form col-sm-12 col-md-6 col-lg-5 mx-sm-2"
    >
      {loginError && (
        <div className="login-error-message">
          <div className="error-content">
            <p className="error-text">
              {t('errorLogin')}{' '}
              <a href={`mailto:${correo || 'info@libelista.com'}`} className="error-link">
                {t('contactaAtencion')}
              </a>
            </p>
          </div>
        </div>
      )}
      {invalidCredentials && (
        <Col lg={12} className="pt-3">
          <p className="credentials">{t('credencialesIncorrectas')}</p>
        </Col>
      )}
      <Row className="container-input pt-4">
        <div className="col-md-12">
          <p className="mb-0 pb-1 inputTitle openSans">{t('correo')}*</p>
          <InputCustom
            className="login-fields"
            type="text"
            placeholder={`${t('correo')}`}
            onChange={(e) => setEmailInput(e.target.value)}
            value={emailInput}
            required
            error={emailInput === '' && t('camporequerido', { ns: 'backoffice' })}
          />
          {emailError && <span className="text-danger fs-6">{t('camporequerido', { ns: 'backoffice' })}</span>}
        </div>
      </Row>
      <Row className="container-input pt-2">
        <div className="col-md-12">
          <p className="mb-0 pb-1 inputTitle openSans">{t('contrasena')}*</p>
          <InputCustom
            type={passwordType}
            placeholder={`${t('contrasena')}`}
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            icon={handleIconType}
            className="px-0.75 login-fields"
            required
            error={password === '' && t('camporequerido', { ns: 'backoffice' })}
          />
          {passwordError && <span className="text-danger fs-6">{t('camporequerido', { ns: 'backoffice' })}</span>}
        </div>
      </Row>
      <Row className="container-input pt-3 pb-4">
        <div className={`col-md-6 text-center px-0 ${fromCarro && 'mx-auto'}`}>
          <Link
            to={libraryPath('recuperar-contrasena')}
            className="d-flex justify-content-center align-items-center"
          >
            <p className="mt-1 blue fs-6 button-text">{t('olvidadoContrasena')}</p>
          </Link>
          <Button type="submit" variant="primary" extraClass="px-3 width-btn-login" loading={loading}>
            {t('iniciarSesion')}
          </Button>
        </div>
        {!fromCarro && (
          <div className="col-md-6 text-center px-0 d-flex align-items-center flex-column">
            <p className="mt-1 fs-6 button-text">{t('noRegistrado')}</p>
            <Link to={libraryPath('registro')} className="button-register width-btn-login ">
              <Button variant="secondary" extraClass="px-3">
                {t('registrate')}
              </Button>
            </Link>
          </div>
        )}
      </Row>
    </Form>
  );
};

export default LoginComp;
